/* eslint-disable @typescript-eslint/no-empty-function */
import React, { createContext, useContext, useState } from 'react';
import UserDoc from '../../../domain/models/UserDoc';
import getUsers from '../../../domain/use_cases/users/getUsers';
import { DocumentData, QueryDocumentSnapshot } from 'firebase/firestore';
import searchUsers from '../../../domain/use_cases/users/searchUsers';
import getUserById from '../../../domain/use_cases/users/getUserById';
import { UserRole, ContextStatus, EProductType } from '@leedpulse-monorepo/enums';
import getUsersFromRoles from '../../../domain/use_cases/users/getUsersFromRoles';
import AuthenticationContext from '../auth/AuthenticationContext';
import loginAsUser from '../../../domain/use_cases/users/loginAsUser';
import { Modal } from 'antd';
import { InfoCircleFilled } from '@ant-design/icons';
import LoginAsMobileModalContent, { getThemePrimaryColor } from '../../components/users_page/LoginAsMobileModalContent';

const UsersContext = createContext({
  searchQuery: '',
  count: 0,
  countAll: 0,
  items: [] as UserDoc[],
  roleConstraint: UserRole.customer as UserRole | undefined,
  selected: null as UserDoc | null,
  lastVisible: {} as QueryDocumentSnapshot<DocumentData, DocumentData>,
  searchResults: [] as UserDoc[],
  noSearchResults: false,
  fetch: () => { },
  fetchRoles: (_: UserRole[]) => { },
  fetchNext: () => { },
  fetchAndSelectById: (_: string) => { },
  select: (_: UserDoc) => { },
  search: (_: string, ignoreRoleConstraint?: boolean) => Promise.resolve([] as UserDoc[]),
  resetSearch: () => { },
  reset: () => { },
  onRoleConstraintChange: (_: UserRole | undefined) => { },
  loginAsUser: (_: UserDoc, _environment: 'computer' | 'mobile' = 'computer', _product: EProductType = EProductType.LEEDFLOW) => { },
  status: ContextStatus.IDLE,
  selectionStatus: ContextStatus.IDLE,
});

export const USERS_FETCH_LIMIT = 20;

export const UsersProvider: React.FC<React.PropsWithChildren> = ({
  children,
}) => {
  const [countAll, setCountAll] = useState(0);
  const [count, setCount] = useState(0);
  const [items, setItems] = useState([] as UserDoc[]);
  const [roleConstraint, setRoleConstraint] = useState<UserRole | undefined>(undefined);
  const [selected, setSelected] = useState(null as UserDoc | null);
  const [lastVisible, setLastVisible] = useState(
    {} as QueryDocumentSnapshot<DocumentData, DocumentData>
  );
  const [searchResults, setSearchResults] = useState([] as UserDoc[]);
  const [noSearchResults, setNoSearchResults] = useState(false);
  const [status, setStatus] = useState(ContextStatus.IDLE);
  const [selectionStatus, setSelectionStatus] = useState(ContextStatus.IDLE);
  const [searchQuery, setSearchQuery] = useState('');

  const authContext = useContext(AuthenticationContext);

  const fetch = async () => {
    setStatus(ContextStatus.LOADING);
    try {
      const useCaseResponse = await getUsers(USERS_FETCH_LIMIT, roleConstraint);
      setItems(useCaseResponse.users);
      setLastVisible(useCaseResponse.lastVisible);
      setCount(useCaseResponse.totalCollectionCount);
      setCountAll(useCaseResponse.totalAllCount);
      setStatus(ContextStatus.SUCCESS);
    } catch (e) {
      setStatus(ContextStatus.ERROR);
    }
  };

  const fetchNext = async () => {
    setStatus(ContextStatus.LOADING);
    try {
      const useCaseResponse = await getUsers(
        USERS_FETCH_LIMIT,
        roleConstraint,
        lastVisible
      );
      setItems([...items, ...useCaseResponse.users]);
      setLastVisible(useCaseResponse.lastVisible);
      setStatus(ContextStatus.SUCCESS);
    } catch (e) {
      setStatus(ContextStatus.ERROR);
    }
  };

  const fetchAndSelectById = async (userId: string) => {
    setSelectionStatus(ContextStatus.LOADING);
    try {
      const useCaseResponse = await getUserById(userId);
      if (useCaseResponse) {
        setSelected(useCaseResponse);
        setSelectionStatus(ContextStatus.SUCCESS);
      } else {
        setSelectionStatus(ContextStatus.ERROR);
      }
    } catch (e) {
      setSelectionStatus(ContextStatus.ERROR);
    }
  };

  const fetchRoles = async (roles: UserRole[]) => {
    setStatus(ContextStatus.LOADING);
    try {
      const useCaseResponse = await getUsersFromRoles(USERS_FETCH_LIMIT, roles, 'firstname', 'asc');
      setItems(useCaseResponse.users);
      setStatus(ContextStatus.SUCCESS);
    } catch (e) {
      setStatus(ContextStatus.ERROR);
    }
  };

  const select = (user: UserDoc) => {
    setSelected(user);
    setSelectionStatus(ContextStatus.SUCCESS);
  };

  const search = async (searchQuery: string, ignoreRoleConstraint = false): Promise<UserDoc[]> => {
    setSearchQuery(searchQuery);
    setStatus(ContextStatus.LOADING);
    try {
      const useCaseResponse = await searchUsers(searchQuery, ignoreRoleConstraint ? undefined : roleConstraint);
      setSearchResults(useCaseResponse);
      setNoSearchResults(useCaseResponse.length === 0);
      setStatus(ContextStatus.SUCCESS);
      return useCaseResponse;
    } catch (e) {
      setStatus(ContextStatus.ERROR);
      return [];
    }
  };

  const resetSearch = () => {
    setSearchResults([]);
    setNoSearchResults(false);
    setSearchQuery('');
  };

  const reset = () => {
    setCount(0);
    setCountAll(0);
    setItems([]);
    setSelected(null);
    setSearchResults([]);
    setNoSearchResults(false);
    setLastVisible({} as QueryDocumentSnapshot<DocumentData, DocumentData>);
    setSelectionStatus(ContextStatus.IDLE);
    setStatus(ContextStatus.IDLE);
    setSearchQuery('');
  };

  const onRoleConstraintChange = (role: UserRole | undefined) => {
    setRoleConstraint(role);
  };

  const handleLoginAsUser = async (user: UserDoc, environment: 'computer' | 'mobile' = 'computer', product: EProductType = EProductType.LEEDFLOW) => {
    if (authContext.authentication) {
      const response = await loginAsUser(user.uid, product, authContext.authentication);
      if (response.success) {
        if (environment === 'mobile') {
          Modal.info({
            title: 'Login as mobile',
            width: 620,
            icon: <InfoCircleFilled style={{ color: getThemePrimaryColor() }} />,
            okButtonProps: { style: { background: getThemePrimaryColor(), borderColor: getThemePrimaryColor() } },
            content: (
              <LoginAsMobileModalContent
                authorizationCode={response.data.authorization_code}
                impersonatedUntil={response.data.impersonatedUntil}
              />
            ),
          });
        } else {
          const authURL = response.data.auth_url;
          window.open(authURL, '_blank', 'noopener,noreferrer');
        }
      }
    }
  };

  return (
    <UsersContext.Provider
      value={{
        searchQuery,
        count,
        countAll,
        items,
        roleConstraint,
        selected,
        lastVisible,
        searchResults,
        noSearchResults,
        fetch,
        fetchNext,
        fetchAndSelectById,
        fetchRoles,
        select,
        search,
        resetSearch,
        reset,
        onRoleConstraintChange,
        loginAsUser: handleLoginAsUser,
        status,
        selectionStatus,
      }}
    >
      {children}
    </UsersContext.Provider>
  );
};

export default UsersContext;
